import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, User, Edit3, Mail, Loader2, Crown, Check,
  Linkedin, Link2, Unlink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { API_URL } from '../lib/utils';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [connectingLinkedin, setConnectingLinkedin] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchLinkedinStatus();
    const linkedinParam = searchParams.get('linkedin');
    if (linkedinParam === 'connected') fetchLinkedinStatus();
  }, [searchParams]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setName(data.name);
      }
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const fetchLinkedinStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/linkedin/status`, { credentials: 'include' });
      if (response.ok) setLinkedinStatus(await response.json());
    } catch (error) { console.error('Error:', error); }
  };

  const updateProfile = async () => {
    try {
      await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name })
      });
      setEditing(false);
      fetchProfile();
      refreshUser();
    } catch (error) { console.error('Error:', error); }
  };

  const connectLinkedin = async () => {
    setConnectingLinkedin(true);
    try {
      const response = await fetch(`${API_URL}/api/linkedin/auth-url`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.auth_url;
      } else {
        alert('LinkedIn integration not available. Contact support to enable it.');
        setConnectingLinkedin(false);
      }
    } catch (error) {
      alert('Failed to connect LinkedIn');
      setConnectingLinkedin(false);
    }
  };

  const disconnectLinkedin = async () => {
    if (!window.confirm('Disconnect LinkedIn?')) return;
    try {
      await fetch(`${API_URL}/api/linkedin/disconnect`, { method: 'POST', credentials: 'include' });
      fetchLinkedinStatus();
    } catch (error) { console.error('Error:', error); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-main">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-muted hover:text-foreground mb-6">
            <ArrowLeft className="w-5 h-5" /> Back
          </button>

          <h1 className="font-heading text-3xl font-bold text-foreground mb-8">Profile Settings</h1>

          <div className="card mb-6">
            <div className="flex items-start gap-4 mb-6">
              {profile?.picture ? (
                <img src={profile.picture} alt="" className="w-20 h-20 rounded-xl" />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-card-hover flex items-center justify-center">
                  <User className="w-10 h-10 text-muted" />
                </div>
              )}
              <div className="flex-1">
                {editing ? (
                  <div className="flex items-center gap-2">
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input flex-1" />
                    <button onClick={updateProfile} className="btn-primary py-2">Save</button>
                    <button onClick={() => { setEditing(false); setName(profile?.name); }} className="btn-secondary py-2">Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h2 className="font-heading text-2xl font-bold text-foreground">{profile?.name}</h2>
                    <button onClick={() => setEditing(true)} className="p-1 text-muted hover:text-primary">
                      <Edit3 className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <p className="text-muted flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4" /> {profile?.email}
                </p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 pt-6 border-t border-border">
              <div>
                <p className="text-sm text-muted mb-1">Member since</p>
                <p className="text-foreground">
                  {profile?.created_at && new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted mb-1">Total sprints</p>
                <p className="text-foreground">{profile?.total_sprints || 0}</p>
              </div>
            </div>
          </div>

          {/* LinkedIn Connection */}
          <div className="card mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#0077b5]/10 flex items-center justify-center">
                  <Linkedin className="w-5 h-5 text-[#0077b5]" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-foreground">LinkedIn</h3>
                  <p className="text-sm text-muted">
                    {linkedinStatus?.connected ? `Connected as ${linkedinStatus.linkedin_name || 'User'}` : 'Not connected'}
                  </p>
                </div>
              </div>
              {linkedinStatus?.connected ? (
                <button onClick={disconnectLinkedin} className="btn-secondary text-sm flex items-center gap-2">
                  <Unlink className="w-4 h-4" /> Disconnect
                </button>
              ) : (
                <button onClick={connectLinkedin} disabled={connectingLinkedin} className="btn-primary text-sm flex items-center gap-2">
                  {connectingLinkedin ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                  Connect
                </button>
              )}
            </div>
            <p className="text-sm text-muted">
              Connect your LinkedIn account to schedule and post content directly from PostedIn.
            </p>
          </div>

          {/* Subscription Card */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold text-foreground">Subscription</h3>
              {profile?.subscription === 'pro' && (
                <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500/10 text-amber-500 text-sm font-medium">
                  <Crown className="w-4 h-4" /> Pro
                </span>
              )}
            </div>
            <div className="p-4 rounded-xl bg-card-hover mb-4">
              <p className="text-foreground font-medium mb-2">{profile?.plan_details?.name} Plan</p>
              <ul className="space-y-2">
                {profile?.plan_details?.features?.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-muted">
                    <Check className="w-4 h-4 text-emerald-500" /> {feature}
                  </li>
                ))}
              </ul>
            </div>
            {profile?.subscription !== 'pro' && (
              <button onClick={() => navigate('/subscription')} className="btn-primary w-full flex items-center justify-center gap-2">
                <Crown className="w-5 h-5" /> Upgrade to Pro
              </button>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
